/**
 * FRONT-2594 x FRONT-2600 — which Canvas item a chat send tells Eddy about.
 *
 * The chat POST carries `activeArtifact: { kind, id }` (parsed server-side by
 * `parseActiveArtifact`, src/artifacts/active-artifact.ts) so Eddy knows which
 * document, deck or sketch the person is looking at and may revise it in place.
 * `id` is a document's filename or a deck/sketch slug — the Canvas item's own id.
 *
 * Sent only when the feature is on, the panel is OPEN and the active item is a
 * kind the backend knows. A Markdown FILE item (`file`) is not a backend kind, so
 * it is never sent. Everything else yields null and the field is ABSENT from the
 * body, which is then byte-identical to a chat without Canvas.
 */
import type { CanvasItem } from './collect-items';

export interface ActiveArtifactRef {
  kind: 'document' | 'deck' | 'sketch';
  id: string;
}

export function activeArtifactForSend(input: {
  enabled: boolean;
  open: boolean;
  items: readonly CanvasItem[];
  activeKey: string | null;
}): ActiveArtifactRef | null {
  if (!input.enabled || !input.open || input.activeKey === null) return null;
  const item = input.items.find((i) => i.key === input.activeKey);
  if (!item || item.kind === 'file' || item.id.length === 0) return null;
  return { kind: item.kind, id: item.id };
}

/**
 * The item a send names, given whether an unsaved Canvas draft is open. While a
 * draft is unsaved the server copy Eddy would read is out of date, so a send that
 * did not pass the save-before-send gate (canvas-send-gate.ts) — a retry, a card
 * button, a plan decision — names no item at all.
 */
export function artifactNamedBySend(draftUnsaved: boolean, open: ActiveArtifactRef | null): ActiveArtifactRef | null {
  return draftUnsaved ? null : open;
}
